export interface Category {
  id: string;
  name: string;
  icon: string;
  color: string;
  theme?: string;
}

export interface Currency {
  code: string;
  symbol: string;
  name: string;
}

export interface Expense {
  id: string;
  amount: number;
  currency: string;
  category: string;
  description: string;
  date: string;
  createdAt?: string;
  updatedAt?: string;
  groupId?: string;
  isRecurring?: boolean;
  recurringParentId?: string | null;
  recurringFrequency?: 'daily' | 'weekly' | 'monthly' | 'yearly';
  paidBy?: string;
  splitType?: 'i_paid_split_equally' | 'i_paid_owed_full' | 'other_paid_split_equally' | 'other_paid_owed_full';
  createdBy?: string;
  isSettlement?: boolean;
  imageUrl?: string | null;
  notes?: string | null;
  sharedWithFriendId?: string | null;
}

export interface ExpenseGroup {
  id: string;
  name: string;
  icon: string;
  members: GroupMember[];
  createdAt: string;
}

export interface GroupMember {
  id: string;
  name: string;
  email: string;
  avatar?: string;
}

export const DEFAULT_CATEGORIES: Category[] = [
  { id: 'food', name: 'Food & Dining', icon: '🍽️', color: 'food' },
  { id: 'transport', name: 'Transport', icon: '🚗', color: 'transport' },
  { id: 'shopping', name: 'Shopping', icon: '🛍️', color: 'shopping' },
  { id: 'entertainment', name: 'Entertainment', icon: '🎬', color: 'entertainment' },
  { id: 'bills', name: 'Bills & Utilities', icon: '📄', color: 'bills' },
  { id: 'health', name: 'Health', icon: '💊', color: 'health' },
  { id: 'travel', name: 'Travel', icon: '✈️', color: 'travel' },
  { id: 'other', name: 'Other', icon: '📦', color: 'other' },
];

export const CURRENCIES: Currency[] = [
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
  { code: 'BGN', symbol: 'лв', name: 'Bulgarian Lev' },
  { code: 'BRL', symbol: 'R$', name: 'Brazilian Real' },
  { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
  { code: 'CHF', symbol: 'CHF', name: 'Swiss Franc' },
  { code: 'CNY', symbol: '¥', name: 'Chinese Yuan' },
  { code: 'CZK', symbol: 'Kč', name: 'Czech Koruna' },
  { code: 'DKK', symbol: 'kr', name: 'Danish Krone' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'HKD', symbol: 'HK$', name: 'Hong Kong Dollar' },
  { code: 'HUF', symbol: 'Ft', name: 'Hungarian Forint' },
  { code: 'IDR', symbol: 'Rp', name: 'Indonesian Rupiah' },
  { code: 'ILS', symbol: '₪', name: 'Israeli Shekel' },
  { code: 'INR', symbol: '₹', name: 'Indian Rupee' },
  { code: 'ISK', symbol: 'kr', name: 'Icelandic Króna' },
  { code: 'JPY', symbol: '¥', name: 'Japanese Yen' },
  { code: 'KRW', symbol: '₩', name: 'South Korean Won' },
  { code: 'MXN', symbol: 'Mex$', name: 'Mexican Peso' },
  { code: 'MYR', symbol: 'RM', name: 'Malaysian Ringgit' },
  { code: 'NOK', symbol: 'kr', name: 'Norwegian Krone' },
  { code: 'NZD', symbol: 'NZ$', name: 'New Zealand Dollar' },
  { code: 'PHP', symbol: '₱', name: 'Philippine Peso' },
  { code: 'PLN', symbol: 'zł', name: 'Polish Złoty' },
  { code: 'RON', symbol: 'lei', name: 'Romanian Leu' },
  { code: 'SEK', symbol: 'kr', name: 'Swedish Krona' },
  { code: 'SGD', symbol: 'S$', name: 'Singapore Dollar' },
  { code: 'THB', symbol: '฿', name: 'Thai Baht' },
  { code: 'TRY', symbol: '₺', name: 'Turkish Lira' },
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'ZAR', symbol: 'R', name: 'South African Rand' },
];

// Exchange rates relative to DKK (base currency)
// These are approximate rates - in production, you'd fetch live rates
export const EXCHANGE_RATES: Record<string, number> = {
  DKK: 1,
  USD: 0.14,  // 1 DKK ≈ 0.14 USD
  EUR: 0.13,  // 1 DKK ≈ 0.13 EUR
};

// Convert amount from one currency to another
export function convertCurrency(amount: number, fromCurrency: string, toCurrency: string): number {
  if (fromCurrency === toCurrency) return amount;
  
  // Convert to DKK first (base currency), then to target
  const amountInDKK = amount / EXCHANGE_RATES[fromCurrency];
  const convertedAmount = amountInDKK * EXCHANGE_RATES[toCurrency];
  
  return Math.round(convertedAmount * 100) / 100; // Round to 2 decimal places
}
